// ─────────────────────────────────────────────
//  caseLanes — how ONE case bar is divided when TWO providers are in the room,
//  and how tall the row has to be to hold them.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ TWO OWNER CORRECTIONS ARE BAKED IN HERE. Read them before changing any
//  number in this file, because the first version got both wrong.
//
//  1. ONE BOX, NOT TWO (2026-08-20). The first attempt drew a paired room as two
//     detached bars. The owner: *"in room 11 the 2 CRNA need to be inside the
//     blue box and inside is missing the supervisor and the surgeon."* So there
//     is exactly ONE bar per case — the case is one thing — and the people are
//     BANDS inside it, stacked above the surgeon / supervising-MD line, which
//     stays. Splitting the box also cost the surgeon line its row; it is back.
//
//  2. THE ROW MAY GROW, AND MUST SHRINK BACK (same conversation). *"If you want
//     to expand the room is ok, as soon as we remove the extra CRNA come to
//     normal size."* Height is therefore a FUNCTION OF HOW MANY PEOPLE ARE IN
//     THE ROOM — not a constant, and not sticky. A single-provider room must be
//     pixel-identical to what it has always drawn (17 of the owner's 19 rooms);
//     the moment a second provider is removed the row returns to it.
//
//  3. NO HIERARCHY IN THE DISPLAY (2026-08-20, same day). *"i dont care who is
//     orienting who, place the 2 crnas."* The two provider bands are the SAME
//     height and the same type. Ordering is stable (the seat first) because a
//     list needs an order, but nothing here may make one band read as
//     subordinate to the other. ⚠️ THIS IS PRESENTATION ONLY — the seat
//     (`ahpId`) and the second-provider fields remain distinct in storage,
//     because a write has to know which slot it is updating and the seat still
//     holds the room for `isSoloCase`.
//
//  ⚠️ AND THE BAND HEIGHT IS AN ACCESSIBILITY DECISION, NOT A LOOK. 24px is WCAG
//  2.2 SC 2.5.8 Target Size (Minimum). Each band holds a name that is a real
//  control (the quick-actions menu) and, for the second provider, a drag handle
//  for their leaving time. At 24px the two name targets are also 24px apart, so
//  they satisfy the Spacing exception rather than overlapping each other's
//  24px circles — which is exactly what the 13px lanes of the first attempt
//  could not do. Do not shrink these to tighten the row.
//
//  Pure: no React, no DOM, no Firestore. jsdom has no layout, so geometry
//  computed inside a component is geometry that cannot be tested.
// ─────────────────────────────────────────────

// What the bar is inset by inside its row, top and bottom. Unchanged, and the
// same whether the row holds one provider or two — the ROW grows, the insets do
// not, so the bar keeps the breathing space it has always had.
export const BAR_INSET_PX = 2;
// `caseBarStyles.bar` is `border: 1px solid`.
export const BAR_BORDER_PX = 1;

// One provider's band inside a paired bar. See the accessibility note above.
export const PROVIDER_BAND_PX = 24;
// The surgeon · supervising-MD line. Not a control, so it is sized for its type
// (10px at line-height 1.1) and nothing else.
export const SUB_BAND_PX = 12;

// The gap above the first band and below the last one.
//
// ⚠️ THIS IS NOT DECORATION — IT IS THE SINGLE-PROVIDER BAR'S OWN BREATHING
// ROOM, MEASURED. A one-provider bar is a 26px content box holding 13px of name
// and 11px of surgeon line, centred by `justify-content: center`: 1px of clear
// space above the text and 1px below it. The paired stack used to be budgeted at
// EXACTLY the height of its three bands, so it had none, and any band that came
// out even a fraction taller than its budget had nowhere to go but under the
// clip. Two pixels is what makes the paired bar breathe like every other bar on
// the board instead of sitting on a knife edge.
export const BAND_PAD_PX = 1;

// Today's row, and the floor this returns to the moment a room holds one person.
//
// ⚠️ MOBILE IS 31, NOT 28, AND THE THREE PIXELS ARE AN ACCESSIBILITY DECISION —
// the same one the paired bands above already make. A bar's pointer target is
// its CLIP box, not its row: row less 2px insets less 2px of border. At 28 that
// clip was 22px and every case bar on a phone was under WCAG 2.2 SC 2.5.8's
// 24px minimum, along with its start- and end-time handles.
//
// ⚠️ AND IT IS 31 RATHER THAN 30 BECAUSE A 24px CLIP CANNOT RENDER A 24px BOX.
// The name's pressable box is padding + line box, and the arithmetic lands on
// exactly 24.00 (5.68 + 12.65 + 5.67). Chromium then floors each of those to
// 1/64 px — 5.671875 + 12.640625 + 5.65625 — and the element measures 23.96875.
// Measured on the deployed board, not reasoned about. Sub-pixel flooring always
// loses, never gains, so the clip needs a pixel of slack for the box inside it
// to clear 24 in the DOM. 31 -> a 25px clip -> ~24.97 measured.
// Desktop was already fine (32 -> 26) and does not move.
//
// This was a KNOWN limit, recorded in caseLanes.test.js rather than fixed,
// because raising it re-spaces the mobile chart and the owner's rule for these
// rows is *"come to normal size"*. The owner asked for the 24px targets on
// 2026-09-06, which settles it: normal size on a phone is now 30.
export const SINGLE_ROW_H = { desktop: 32, mobile: 31 };

// What a clip must carry ABOVE the 24px minimum for the box inside it to still
// measure 24 after the layout engine floors every length to 1/64 px. One pixel
// is far more than the ~0.03 actually lost, and cheaper than a rounding rule
// that has to be right in three places.
export const SUBPIXEL_SLACK_PX = 1;

const num = (n, fallback) => (typeof n === "number" && Number.isFinite(n) ? n : fallback);

/** Does this case put two providers in the room? Presentation's only question. */
export function hasSecondProvider(c) {
  return Boolean(String(c?.orientee?.name || "").trim());
}

/**
 * How tall the row must be.
 *
 * ⚠️ THE SINGLE-PROVIDER ANSWER IS A CONSTANT, ON PURPOSE. It is not derived
 * from the band arithmetic below, because deriving it would let a future tweak
 * to a band silently move 17 of 19 rooms. The owner asked for those rows to
 * come back to "normal size", and this is what normal size is.
 *
 * ⚠️ MOBILE DROPS THE SURGEON LINE, exactly as it does today (`isMobile` has
 * always suppressed the sub-line), so a paired mobile row buys two bands and
 * nothing else.
 */
export function rowHeightFor({ paired = false, isMobile = false } = {}) {
  if (!paired) return isMobile ? SINGLE_ROW_H.mobile : SINGLE_ROW_H.desktop;
  const sub = isMobile ? 0 : SUB_BAND_PX;
  return (
    PROVIDER_BAND_PX * 2 +
    sub +
    BAND_PAD_PX * 2 +
    BAR_BORDER_PX * 2 +
    BAR_INSET_PX * 2
  );
}

/**
 * The row height for a whole ROOM — the tallest its cases need.
 *
 * A room can hold several cases back to back on one row; if any ONE of them has
 * two providers the row has to be tall enough for it, or that bar is clipped.
 */
export function roomRowHeight(cases = [], isMobile = false) {
  const list = Array.isArray(cases) ? cases : [];
  const paired = list.some(hasSecondProvider);
  return rowHeightFor({ paired, isMobile });
}

/**
 * Where each band sits inside a PAIRED bar's content box, in px from its top.
 *
 * Returns null for a single-provider bar — that one keeps its original
 * centred-flex layout untouched, which is the only way to guarantee "pixel
 * identical to today" for the 17 rooms that are not paired.
 *
 * @returns null | { seat: {top,height}, second: {top,height}, sub: {top,height}|null }
 */
export function barBands({ paired = false, isMobile = false } = {}) {
  if (!paired) return null;
  const seat = { top: BAND_PAD_PX, height: PROVIDER_BAND_PX };
  const second = { top: BAND_PAD_PX + PROVIDER_BAND_PX, height: PROVIDER_BAND_PX };
  const sub = isMobile
    ? null
    : { top: BAND_PAD_PX + PROVIDER_BAND_PX * 2, height: SUB_BAND_PX };
  return { seat, second, sub };
}

/**
 * The window the SECOND provider is in this room for, or null when the room
 * holds one person.
 *
 * ⚠️ THEIR HOURS ARE THEIR OWN. `case.orientee.departMin` is resolved by
 * `timeline.orienteeDepartMinutes` from that person's OWN explicit leaving
 * time, their OWN shift code, or their OWN roster entry — never the seat's.
 * Borrowing the other provider's end would print a confident wrong answer about
 * when a room loses a person, which is the fact the second band exists to show.
 *
 * ⚠️ NO DEPARTURE IS A REAL ANSWER, NOT A MISSING ONE, and it is why this
 * returns `openEnded` instead of a number. A sheet cell that names a second
 * CRNA and no leaving time is the common case on today's board. The band then
 * draws NO HATCH AT ALL — in this app's visual language an unhatched bar means
 * "still here", and a hatch covering the whole line would say the opposite.
 *
 * @returns null | { startMin, endMin, openEnded }
 */
export function secondCoverage(c) {
  if (!hasSecondProvider(c)) return null;

  const startMin = num(c?.startMin, null);
  const endMin = num(c?.endMin, null);
  if (startMin == null || endMin == null || !(endMin > startMin)) return null;

  const depart = num(c?.orientee?.departMin, null);
  // Nothing recorded, or a recorded time that is not inside this case's window
  // (a shift that ended before the case began is data we cannot draw). Both
  // fail OPEN-ENDED rather than inventing an edge.
  if (depart == null || depart <= startMin) return { startMin, endMin, openEnded: true };
  // They are here past the end of the case: the room stops needing them when
  // the case does, and that IS known, so it is closed.
  if (depart >= endMin) return { startMin, endMin, openEnded: false };
  return { startMin, endMin: depart, openEnded: false };
}

// ── THE SINGLE-PROVIDER BAR'S NAME TARGET ───────────────────────────────────
//
// The two type sizes a ONE-provider bar stacks, as LINE-BOX heights in px. A
// line box is exactly its `line-height`, so these are resolved values and not
// estimates: `caseBarStyles.barLabel` is 11px x 1.15 and `barSub` is 10px x 1.1.
//
// ⚠️ IF EITHER RULE'S TYPE CHANGES, CHANGE IT HERE TOO. `caseBarStyles.test.js`
// asserts the two agree, because nothing in a browser ever would — the bar would
// simply start clipping a target that still measured 24px in the DOM.
export const LABEL_LINE_PX = 11 * 1.15; // 12.65
export const SUB_LINE_PX = 10 * 1.1; //    11

/**
 * The bar's own clipping box, in px: the row, less its insets and its border.
 *
 * ⚠️ THIS IS A CEILING, NOT A SUGGESTION. `caseBarStyles.bar` sets
 * `overflow: hidden` (it has to — the hatch, the handles and the name's
 * ellipsis all depend on it), and a clipped box is not hit-tested. Nothing
 * inside a bar can be a bigger pointer target than this number, whatever its
 * `getBoundingClientRect()` says.
 */
export function singleBarInnerPx(isMobile = false) {
  return rowHeightFor({ isMobile }) - BAR_INSET_PX * 2 - BAR_BORDER_PX * 2;
}

/**
 * Padding that grows the CRNA name's PRESSABLE box to the full height of a
 * single-provider bar, plus the equal negative margins that hand every pixel of
 * it straight back to the layout.
 *
 * Measured in Chrome on the deployed board, 2026-08-20: the name trigger on a
 * one-provider bar was **186 x 13** — a real control (it opens that person's
 * quick-actions menu) at half the 24px WCAG 2.2 SC 2.5.8 Target Size (Minimum).
 * The paired bands have never had the bug: they hand the button a 24px
 * `line-height` (`PROVIDER_BAND_PX`), so their triggers already measure 24.
 *
 * ⚠️ THE ROW MAY NOT GROW, SO THE BOX HAS TO. `SINGLE_ROW_H` is 32/28 by owner
 * decision — *"as soon as we remove the extra CRNA come to normal size"* — and
 * 17 of 19 rooms are on it. Adding height to reach 24px would re-space the whole
 * chart. Padding grows only the box; the negative margin means the flex column
 * above still lays this item out at exactly `LABEL_LINE_PX`, so the name, the
 * surgeon line under it and every neighbouring bar stay where they are. The
 * arithmetic is `padding + line + padding` in and `-padding` out: it cancels,
 * exactly, by construction rather than by measurement.
 *
 * ⚠️ AND PADDING IS THE ONLY MECHANISM AVAILABLE HERE. The obvious alternative —
 * an absolutely-positioned overlay child that stretches past the button — CANNOT
 * WORK: the caller spreads `barLabel` onto the trigger, which carries
 * `overflow: hidden` for `text-overflow: ellipsis`, so the button clips its own
 * descendants back to the 13px line. Padding is part of the element's own box,
 * not its content, so the button's overflow rule does not touch it.
 *
 * ⚠️ THE SPLIT IS NOT SYMMETRIC, AND A SYMMETRIC ONE WOULD HAVE BEEN HALF EATEN.
 * The name is NOT centred in the bar — `justify-content: center` centres the
 * name + surgeon-line STACK, which leaves the name sitting ~1px below the clip's
 * top edge and ~12px above its bottom. A tidy `padding: 6px 0` would put 5 of
 * those 6 top pixels outside `bar`'s `overflow: hidden`, and clipped pixels are
 * not pressable: a box that measured 25px would have been a 20px target. So the
 * padding is derived — everything the flex centring left ABOVE the name, and
 * everything left BELOW it — which lands the box flush inside the clip in every
 * variant of the bar (with the surgeon line, without it, and on mobile).
 *
 * ⚠️ MOBILE CANNOT REACH 24 AND THIS DOES NOT PRETEND OTHERWISE. A 28px row is a
 * 22px clip, so the honest ceiling there is 22. Fixing that means changing
 * `SINGLE_ROW_H.mobile`, which is the owner's number, not this function's.
 *
 * @returns {{paddingTop:number, paddingBottom:number, marginTop:number, marginBottom:number}}
 */
export function nameHitPad({ isMobile = false, subShown = false } = {}) {
  const inner = singleBarInnerPx(isMobile);
  // What `justify-content: center` leaves above the stack — and therefore above
  // the name, which is the first item in it.
  const stack = LABEL_LINE_PX + (subShown ? SUB_LINE_PX : 0);
  const above = px(Math.max(0, (inner - stack) / 2));
  // Everything from the bottom of the name's line box to the bottom of the clip:
  // the surgeon line's row when there is one, the leftover centring gap when
  // there is not. Both are space the bar already owns and no control uses.
  const below = px(Math.max(0, inner - above - LABEL_LINE_PX));
  return { paddingTop: above, paddingBottom: below, marginTop: -above, marginBottom: -below };
}

// 11 x 1.15 is 12.649999999999999 in binary floating point, and React writes
// inline styles verbatim — without this the board would ship
// `padding-top: 1.1750000000000007px`. Two decimals is far below a device pixel
// and it keeps the DOM readable for whoever next measures this bar.
function px(n) {
  return Math.round(n * 100) / 100;
}
